import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getToken, updateUserData } from '@/utils/auth';
import { logger } from '@/utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ChangeEmailForm = ({ currentEmail, onEmailChanged }) => {
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const validate = () => {
    if (!newEmail || !password) {
      return 'Please fill in both fields.';
    }
    // Basic email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      return 'Please enter a valid email address.';
    }
    if (newEmail.trim().toLowerCase() === currentEmail.toLowerCase()) {
      return 'New email must be different from your current email.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const token = getToken();
      const { data } = await axios.post(
        `${API}/auth/change-email`,
        { new_email: newEmail.trim(), password },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      updateUserData({ email: data.new_email, email_verified: false });
      setSuccess(data.message || 'Email updated. Please check your inbox to verify.');
      setNewEmail('');
      setPassword('');
      toast.success('Email updated. Verification link sent.');
      if (onEmailChanged) onEmailChanged(data.new_email);
    } catch (err) {
      logger.error('Change email failed:', err);
      const detail = err.response?.data?.detail || 'Failed to update email. Try again.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-email-form">
      <div>
        <h3 className="text-sm font-medium text-[#71717A] uppercase tracking-wider">
          Change Email
        </h3>
        <p className="text-xs text-[#71717A] mt-1">
          Current: <span className="text-[#09090B] font-mono">{currentEmail}</span>
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="new-email">New Email Address</Label>
        <Input
          id="new-email"
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="you@newdomain.com"
          className="border-[#E4E4E7]"
          data-testid="new-email-input"
          autoComplete="email"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="change-email-password">Confirm Password</Label>
        <Input
          id="change-email-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your current password"
          className="border-[#E4E4E7]"
          data-testid="change-email-password-input"
          autoComplete="current-password"
        />
      </div>

      {error && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2"
          data-testid="change-email-error"
        >
          {error}
        </p>
      )}

      {success && (
        <p
          className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2"
          data-testid="change-email-success"
        >
          {success}
        </p>
      )}

      <Button
        type="submit"
        disabled={loading}
        className="bg-[#0047FF] hover:bg-[#0037CC] text-white"
        data-testid="submit-change-email-button"
      >
        {loading ? 'Updating...' : 'Update Email'}
      </Button>
    </form>
  );
};
